import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import { fetchYojanaApprovals } from '../utils/api';
import { formatLocalDateTime } from '../utils/dateTime';

const getCurrentUserId = () => {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.userId ?? parsed?.id ?? parsed?.appUserId ?? null;
  } catch {
    return null;
  }
};

/** Full create draft from approval row (backend stores it in updatedDataJson). */
const parseUpdatedDraft = (row) => {
  const raw = row?.updatedDataJson;
  if (raw == null || raw === '') return null;
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const MyYojanaRequests = () => {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const handleEditRejected = (row) => {
    if (row.yojanaId != null && row.yojanaId !== '') {
      navigate('/dashboard/schemes', { state: { editYojanaId: row.yojanaId } });
      return;
    }
    const draft = parseUpdatedDraft(row);
    navigate('/dashboard/schemes', { state: { resubmitCreateDraft: draft } });
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetchYojanaApprovals();
        if (cancelled) return;
        const raw = Array.isArray(res.data) ? res.data : [];
        const currentUserId = getCurrentUserId();
        const mine = currentUserId != null
          ? raw.filter((row) => String(row.requestedById) === String(currentUserId))
          : raw;
        setList(mine);
      } catch (err) {
        console.error('Error fetching my yojana requests:', err);
        if (!cancelled) {
          setError(err.response?.data?.message || 'Failed to load your requests.');
          setList([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        My Yojana Requests
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        List of approval requests you created. Rejected requests show the rejection reason.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Id</TableCell>
                <TableCell>Yojana Id</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Rejection reason</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No requests found.
                  </TableCell>
                </TableRow>
              ) : (
                list.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.id}</TableCell>
                    <TableCell>{row.yojanaId}</TableCell>
                    <TableCell>{formatLocalDateTime(row.requestedDate)}</TableCell>
                    <TableCell>{row.action ?? '—'}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={row.status ?? 'PENDING'}
                        color={
                          row.status === 'APPROVED'
                            ? 'success'
                            : row.status === 'REJECTED'
                              ? 'error'
                              : 'default'
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {row.status === 'REJECTED' && row.rejectMessage ? (
                        <Typography variant="body2" color="error">
                          {row.rejectMessage}
                        </Typography>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {row.status === 'REJECTED' && (
                        <Tooltip title="Edit yojana">
                          <IconButton
                            size="small"
                            onClick={() => handleEditRejected(row)}
                            aria-label="Edit yojana"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default MyYojanaRequests;
